import {pushEvent} from "tracking/core/dispatcher";
import {EVENT_NAMES} from "tracking/core/eventNames";
import {NavigationPayload, PushOptions} from "tracking/core/types";

type NavigationLevel = 0 | 1 | 2 | "first" | "second" | "third";


export type SingleLabelInput =
    | string
    | { navigation_element?: string | null }
    | null
    | undefined;

export function getNavigationElement(input: SingleLabelInput): string | undefined {
    if (typeof input === "string") return input || undefined;
    if (input == null) return undefined;
    return input.navigation_element ?? undefined;
}

export function buildSingleLabelPayload(label?: string | null): NavigationPayload {
    return {navigation_element: label ?? undefined};
}

export function buildNavigationPayload(
    elementLocation?: string | null,
    navigationElement?: string | null,
    navigationSubElementOne?: string | null,
    navigationSubElementTwo?: string | null,
): NavigationPayload {
    const payload: NavigationPayload = {
        element_location: elementLocation ?? undefined,
    };

    if (navigationSubElementTwo) {
        payload.navigation_sub_element_two = navigationSubElementTwo;
        return payload;
    }

    if (navigationSubElementOne) {
        payload.navigation_sub_element_one = navigationSubElementOne;
        return payload;
    }

    payload.navigation_element = navigationElement ?? undefined;
    return payload;
}

function normalizeLevel(level: NavigationLevel): 0 | 1 | 2 {
    if (typeof level === "number") {
        if (level <= 0) return 0;
        if (level >= 2) return 2;
        return 1;
    }

    switch (level) {
        case "first":
            return 0;
        case "second":
            return 1;
        case "third":
            return 2;
    }
}

function pushLevelNavigation(
    elementLocation: string,
    level: 0 | 1 | 2,
    label?: string | null,
    options?: PushOptions
) {
    if (level === 2) {
        dataLayerNavigation(elementLocation, undefined, undefined, label, options);
        return;
    }

    if (level === 1) {
        dataLayerNavigation(elementLocation, undefined, label, undefined, options);
        return;
    }

    dataLayerNavigation(elementLocation, label, undefined, undefined, options);
}

function pushSingleLabelEvent(
    eventName: typeof EVENT_NAMES.META_NAVIGATION
        | typeof EVENT_NAMES.BREADCRUMB
        | typeof EVENT_NAMES.TOPIC_NAVIGATION,
    navigationElement: string | null | undefined,
    options?: PushOptions
) {
    pushEvent(eventName, buildSingleLabelPayload(navigationElement), options);
}

function pushNamedNavigation(
    elementLocation: "Main Navigation" | "Main Navigation Mobile",
    level: NavigationLevel,
    label?: string | null,
    options?: PushOptions
) {
    try {
        pushLevelNavigation(elementLocation, normalizeLevel(level), label, options);
    } catch (e) {
        if (process.env.NODE_ENV !== "production") {
            console.warn(`${elementLocation} tracking failed`, e);
        }
    }
}

export function dataLayerNavigation(
    elementLocation: string | null | undefined,
    navigationElement: string | null | undefined,
    navigationSubElementOne?: string | null,
    navigationSubElementTwo?: string | null,
    options?: PushOptions
) {
    const payload = buildNavigationPayload(
        elementLocation,
        navigationElement,
        navigationSubElementOne,
        navigationSubElementTwo
    );
    pushEvent(EVENT_NAMES.NAVIGATION, payload, options);
}

export const toSingleLabelPayload = (input: SingleLabelInput): NavigationPayload =>
    buildSingleLabelPayload(getNavigationElement(input));

export function toMainNavigationPayload(
    label?: string | null,
    level: number = 0,
    isMobile: boolean = false
): NavigationPayload {
    const location = isMobile ? "Main Navigation Mobile" : "Main Navigation";
    if (level === 2) return buildNavigationPayload(location, undefined, undefined, label);
    if (level === 1) return buildNavigationPayload(location, undefined, label, undefined);
    return buildNavigationPayload(location, label, undefined, undefined);
}


export function dataLayerMetaNavigation(
    navigationElement: string | null | undefined,
    options?: PushOptions
) {
    pushSingleLabelEvent(EVENT_NAMES.META_NAVIGATION, navigationElement, options);
}

export function dataLayerBreadcrumb(
    navigationElement: string | null | undefined,
    options?: PushOptions
) {
    pushSingleLabelEvent(EVENT_NAMES.BREADCRUMB, navigationElement, options);
}

export function dataLayerTopicNavigation(
    navigationElement: string | null | undefined,
    options?: PushOptions
) {
    pushSingleLabelEvent(EVENT_NAMES.TOPIC_NAVIGATION, navigationElement, options);
}

export function dataLayerNavigationMain(
    level: NavigationLevel,
    label?: string | null,
    options?: PushOptions
) {
    pushNamedNavigation("Main Navigation", level, label, options);
}

export function dataLayerNavigationMobileLink(
    level: NavigationLevel,
    label?: string | null,
    options?: PushOptions
) {
    pushNamedNavigation("Main Navigation Mobile", level, label, options);
}

export function dataLayerClickLogo(options?: PushOptions) {
    return pushEvent(EVENT_NAMES.CLICK_LOGO, {}, options);
}